import { inflateRaw } from "pako";

// Native js pick function
export const pick = (object, keys) => {
  // Defensive guard: callers may pass undefined keys from partial customer configs.
  if (!Array.isArray(keys)) {
    return {};
  }
  return keys.reduce((obj, key) => {
    if (object && Object.prototype.hasOwnProperty.call(object, key)) {
      obj[key] = object[key];
    }
    return obj;
  }, {});
};

// Native js sum of array
export const sum = array => {
  return array.reduce((a, b) => a + b);
};

// Whether the user still has storage quota to import new exams. The tier
// fallbacks cover users whose memory_included has not been synced yet.
export const userHasEnoughSpace = user => {
  if (!user) return false;

  const tierFallbackMemory = {
    free: 1 * 10 ** 9,
    base: 5 * 10 ** 9,
    pro: 100 * 10 ** 9
  };

  const includedMemory =
    (user.memory_included || 0) ||
    tierFallbackMemory[(user.subscription_tier || "").toLowerCase()] ||
    0;
  const totalMemory = includedMemory + (user.memory_bonus || 0);
  const usedMemory = user.memory_used || 0;

  return totalMemory > usedMemory;
};

// Native unique: receive a flat array and returns a unique values array
export const unique = array => {
  if (!array) return;
  return array.filter((v, i, self) => {
    return self.indexOf(v) === i;
  });
};

// Convert mask blob (as uint8) to uint16
export const toUint16 = uint8blob => {
  let uint16 = new Uint16Array(uint8blob.length / 2);
  for (let i = 0; i < uint16.length; i++) {
    uint16[i] = uint8blob[i * 2] + (uint8blob[i * 2 + 1] << 8);
  }
  return uint16;
};

// Decompress deflate-raw compressed blobs into a Uint8Array
export const decompressBlob = async blob => {
  if (!blob) {
    return blob;
  }
  const uint8 = blob instanceof Uint8Array ? blob : new Uint8Array(blob);

  if (typeof DecompressionStream !== "undefined") {
    const stream = new Blob([uint8]).stream();
    const decompressedStream = stream.pipeThrough(
      new DecompressionStream("deflate-raw")
    );
    const arrayBuffer = await new Response(decompressedStream).arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  // Fallback for browsers without DecompressionStream support (e.g. older Safari)
  return inflateRaw(uint8);
};

// Detact mobile/tablet browser
// https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
export const isMobileOrTablet = () => {
  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Opera Mini/i,
    /Windows Phone/i,
    /IEMobile/i,
    /WPDesktop/i
  ];

  return toMatch.some(toMatchItem => {
    return navigator.userAgent.match(toMatchItem);
  });
};

// Check if a DOM element is an editable target (input, textarea, etc.)
export const isEditableTarget = target => {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tagName = target.tagName;
  return tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT";
};

export const triggerDownloadFile = response => {
  const disposition =
    response.headers && typeof response.headers.get === "function"
      ? response.headers.get("Content-Disposition") || ""
      : "";
  const raw = disposition.includes("filename=")
    ? disposition.split("filename=")[1]
    : "";
  const filename = raw ? raw.replace(/^["']|["']$/g, "") : "download";
  const blob = response.body || response.data || response;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const isDiscussionsEnabled = () => {
  const features = process.env.APP_CUSTOMER?.enabledFeatures;
  if (Array.isArray(features)) {
    return features.includes("discussions");
  }
  if (features && Object.prototype.hasOwnProperty.call(features, "discussions")) {
    return !!features.discussions;
  }
  return (
    process.env.VUE_APP_FEATURE_DISCUSSIONS?.toUpperCase() === "TRUE" ||
    process.env.BACKEND_ENABLED_FEATURES__DISCUSSIONS?.toUpperCase() === "TRUE"
  );
};

export const toReadableDate = date => {
  const locale = navigator.language || "en-US";
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dateOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: timezone
  };

  const timeOptions = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
    timeZone: timezone
  };

  const readableDate = date.toLocaleDateString(locale, dateOptions);
  const readableTime = date.toLocaleTimeString(locale, timeOptions);

  return `${readableDate} at ${readableTime}`;
};
